import type { OptinSchedule, OptinStatus } from "@/types/optins";
import type { Dayjs } from "dayjs";

import dayjs from "@/utils/configuredDayjs";

/** The fields status depends on; any optin-shaped value satisfies it. */
export type SchedulableOptin = {
  active: boolean;
  schedule?: OptinSchedule | null;
};

function toInstant(value: string | undefined | null) {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/**
 * The state to show for an exchange right now.
 *
 * Mirrors the API's `resolveOptinStatus` branch for branch — `active` is the
 * on/off switch and the window only narrows it:
 *
 *   active: false               -> inactive
 *   active: true, no dates      -> active
 *   active: true, before start  -> scheduled
 *   active: true, inside window -> active
 *   active: true, after end     -> inactive
 *
 * The API sends this as `optin.status`, which should be preferred when it is
 * present. This exists for the cases that have no server answer to read: a
 * window being edited but not yet saved, and responses predating the field.
 */
export function resolveOptinStatus(
  { active, schedule }: SchedulableOptin,
  now: Dayjs = dayjs(),
): OptinStatus {
  if (!active) return "inactive";

  const startAt = toInstant(schedule?.startAt);
  if (startAt && now.isBefore(startAt)) return "scheduled";

  // End is exclusive: a window set to end at midnight stops at midnight
  // rather than running a millisecond into the next day.
  const endAt = toInstant(schedule?.endAt);
  if (endAt && !now.isBefore(endAt)) return "inactive";

  return "active";
}

/**
 * Whether a stored window actually bounds anything.
 *
 * Used to decide whether an update needs to mention `schedule` at all: an
 * exchange that never had a window has nothing to clear, so the field is left
 * out of the request entirely.
 */
export function hasStoredWindow(schedule?: OptinSchedule | null): boolean {
  return Boolean(schedule?.startAt || schedule?.endAt);
}

/**
 * The status to show for an optin.
 *
 * A window is re-evaluated against the current clock rather than trusting
 * `optin.status`: the API computes that when it responds, so a list fetched at
 * 11:58 would keep calling a window "scheduled" past its midday start. With no
 * window there is nothing to go stale — the server's answer is `active` itself —
 * so it is used as-is.
 */
export function optinStatus(
  optin: SchedulableOptin & { status?: OptinStatus },
  now: Dayjs = dayjs(),
): OptinStatus {
  if (hasStoredWindow(optin.schedule)) {
    return resolveOptinStatus(optin, now);
  }

  return optin.status ?? resolveOptinStatus(optin, now);
}

/**
 * When a scheduled exchange starts, written the way the broadcast list writes
 * its send time — "6/29/26 at 5:00am PDT".
 *
 * Empty unless the exchange is actually scheduled, so only those cards carry
 * the line. Read in the zone the window was authored in, not the viewer's.
 */
export function scheduledStartText(
  optin: SchedulableOptin & { status?: OptinStatus },
  now: Dayjs = dayjs(),
): string {
  if (optinStatus(optin, now) !== "scheduled") return "";

  const startAt = toInstant(optin.schedule?.startAt);
  if (!startAt) return "";

  const zone = optin.schedule?.timezone;
  const local = zone ? startAt.tz(zone) : startAt;

  return `${local.format("M/D/YY")} at ${local.format("h:mma")} ${local.format("z")}`;
}

/**
 * The bound worth naming next to a status chip: what a running exchange is
 * counting down to, or when a scheduled one begins.
 *
 * Read in the zone the window was authored in and labelled with it, so a
 * window set for "11:59 PM Pacific" never reads as the viewer's own 11:59.
 *
 * An inactive exchange names nothing. Its dates have either passed or never
 * applied, so quoting one alongside "Draft" would read as a promise the
 * exchange is not keeping.
 */
export function scheduleTimingLabel(
  optin: SchedulableOptin & { status?: OptinStatus },
  now: Dayjs = dayjs(),
): string {
  const { schedule } = optin;
  if (!schedule) return "";

  const status = optinStatus(optin, now);
  if (status === "inactive") return "";

  const bound =
    status === "scheduled"
      ? { verb: "starts", at: schedule.startAt }
      : { verb: "ends", at: schedule.endAt };

  const instant = toInstant(bound.at);
  if (!instant) return "";

  const zone = schedule.timezone;
  const local = zone ? instant.tz(zone) : instant;
  const abbreviation = zone ? local.format("z") : "";

  return [
    bound.verb,
    local.format("MMM D"),
    "at",
    local.format("h:mm A"),
    abbreviation,
  ]
    .filter(Boolean)
    .join(" ");
}
